import React, { useState, useRef, forwardRef } from 'react';
import { useInViewport } from 'ahooks';
import { OmitStylesHTMLAttributes } from '../../types';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../utils/cn';

const readMoreVariants = cva('rocco-read-more', {
    variants: {
        variant: {
            default:
                'transition-all duration-300 motion-reduce:transition-none',
            noTransition: '',
        },
    },
    defaultVariants: {
        variant: 'default',
    },
});

export interface ReadMoreProps {
    /**
     * Whether the content is clamped (truncated) initially
     * @default true
     */
    clamped?: boolean;
    /**
     * Maximum number of lines to show when clamped
     * @default 3
     */
    maxLines?: number;
    /**
     * Maximum height in pixels (alternative to maxLines)
     */
    maxHeight?: number;
    /**
     * Whether to auto-scroll to the component when toggling
     * @default true
     */
    autoScroll?: boolean;
    /**
     * Text for the "read more" button
     * @default "Read more"
     */
    readMoreText?: string;
    /**
     * Text for the "read less" button
     * @default "Read less"
     */
    readLessText?: string;
    /**
     * Offset from the top of the viewport when scrolling
     * @default 0
     */
    scrollOffset?: number;
    /**
     * Threshold for viewport intersection detection
     * @default 0.75
     */
    scrollViewportThreshold?: number;
    /**
     * Whether to show a button to toggle (false means click on content to toggle)
     * @default true
     */
    buttonRequired?: boolean;
    /**
     * Callback when the clamped state changes
     */
    onToggle?: (isClamped: boolean) => void;
    /**
     * Custom class for the toggle button
     */
    buttonClassName?: string;
    /**
     * Custom class for the component root
     */
    className?: string;
    /**
     * Whether to always show the toggle button
     * @default false
     */
    alwaysShowButton?: boolean;
}

interface ReadMoreComponentProps
    extends OmitStylesHTMLAttributes<HTMLDivElement>,
        ReadMoreProps,
        VariantProps<typeof readMoreVariants> {}

/**
 * A component that truncates content and provides "Read more" / "Read less" buttons to expand or collapse the content.
 * Uses Tailwind's line-clamp utilities for better browser compatibility.
 * Automatically applies rich text styling for HTML content.
 */
export const ReadMore = forwardRef<HTMLDivElement, ReadMoreComponentProps>(
    ({ children, variant, className, buttonClassName, ...props }, ref) => {
        const {
            clamped: clampedProp = true,
            maxLines = 3,
            maxHeight,
            autoScroll = true,
            readMoreText = 'Read more',
            readLessText = 'Read less',
            scrollOffset = 0,
            scrollViewportThreshold = 0.75,
            buttonRequired = true,
            onToggle,
            alwaysShowButton = false,
        } = props;

        const contentRef = useRef<HTMLDivElement>(null);
        const [clamped, setClamped] = useState(clampedProp);

        const [inViewport] = useInViewport(contentRef, {
            threshold: scrollViewportThreshold,
        });

        const handleToggle = (event: React.MouseEvent<Element, MouseEvent>) => {
            event.stopPropagation();
            const newClampedState = !clamped;
            setClamped(newClampedState);

            // Call the onToggle callback if provided
            if (onToggle) {
                onToggle(newClampedState);
            }

            if (clamped && autoScroll) {
                const element = contentRef.current;
                if (element && !inViewport) {
                    const top = element.getBoundingClientRect().top;
                    window.scrollTo({
                        behavior: 'smooth',
                        top: top + window.scrollY - scrollOffset,
                    });
                }
            }
        };

        // Get rich text classes for proper styling
        const getRichTextClasses = () => {
            return cn(
                '[&_p:not(:last-child)]:mb-4',
                '[&_ul]:list-disc [&_ol]:list-decimal',
                '[&_ul]:pl-5 [&_ol]:pl-5',
                '[&_li]:mb-2',
                '[&_h1]:text-2xl [&_h1]:font-semibold [&_h1]:mb-4',
                '[&_h2]:text-xl [&_h2]:font-semibold [&_h2]:mb-3',
                '[&_h3]:text-lg [&_h3]:font-semibold [&_h3]:mb-3',
                '[&_h4]:font-semibold [&_h4]:mb-2',
                '[&_a]:underline [&_a]:text-foreground',
                '[&_blockquote]:pl-4 [&_blockquote]:border-l-4 [&_blockquote]:border-gray-300 [&_blockquote]:italic [&_blockquote]:my-4',
            );
        };

        return (
            <div ref={ref} className={className}>
                <div
                    ref={contentRef}
                    aria-expanded={!clamped}
                    className={cn(
                        readMoreVariants({ variant }),
                        clamped && maxLines && maxLines <= 6
                            ? `line-clamp-${maxLines}`
                            : '',
                        maxHeight && clamped ? 'overflow-hidden' : '',
                        !buttonRequired ? 'cursor-pointer' : '',
                        // Always apply rich text styles
                        'prose-sm prose-gray dark:prose-invert max-w-none',
                        getRichTextClasses(),
                    )}
                    style={
                        clamped
                            ? maxLines && maxLines > 6
                                ? {
                                      overflow: 'hidden',
                                      display: '-webkit-box',
                                      WebkitBoxOrient: 'vertical',
                                      WebkitLineClamp: maxLines,
                                      ...(maxHeight
                                          ? { maxHeight: `${maxHeight}px` }
                                          : {}),
                                  }
                                : maxHeight
                                  ? { maxHeight: `${maxHeight}px` }
                                  : undefined
                            : undefined
                    }
                    onClick={(e: React.MouseEvent<HTMLDivElement>) => {
                        if (!buttonRequired) handleToggle(e);
                    }}
                >
                    {children}
                </div>

                {buttonRequired && (alwaysShowButton || !clamped) && (
                    <button
                        type="button"
                        onClick={(e: React.MouseEvent<HTMLButtonElement>) =>
                            handleToggle(e)
                        }
                        className={cn(
                            'mt-2 md:mt-4 bg-transparent hover:underline underline-offset-2 text-foreground',
                            buttonClassName,
                        )}
                    >
                        {clamped ? readMoreText : readLessText}
                    </button>
                )}
            </div>
        );
    },
);

ReadMore.displayName = 'ReadMore';
